import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { Building2, MapPin, Plus } from 'lucide-react';
import { adminApi, type AdminDepartment, type AdminLocation } from '@/api/endpoints';
import { queryKeys } from '@/api/queryKeys';
import { usePermission } from '@/auth/AuthProvider';
import { PERMISSIONS } from '@/auth/permissions';
import { Button } from '@/design-system/Button';
import { DataTable, type Column } from '@/design-system/DataTable';
import { InlineError } from '@/design-system/Feedback';
import { Checkbox, Select, TextInput } from '@/design-system/Inputs';
import { countryOptions, INDIA_STATES } from '@/lib/geo';
import { Dialog } from '@/design-system/Overlay';
import { ActiveBadge, asPage, SettingsCard, useSettingsSave } from './shared';

/**
 * Departments and locations.
 *
 * Nothing is deleted: employees and assets point at these, so retiring one is unticking "Active",
 * which removes it from the pickers and keeps every existing reference. Codes are fixed at creation
 * because imports and HR feeds key on them.
 */
export function OrganizationSection() {
  const canWrite = usePermission(PERMISSIONS.ORG_WRITE);
  const [department, setDepartment] = useState<AdminDepartment | 'new' | null>(null);
  const [location, setLocation] = useState<AdminLocation | 'new' | null>(null);

  const departments = useQuery({
    queryKey: queryKeys.admin.departments(),
    queryFn: adminApi.departments,
  });
  const locations = useQuery({ queryKey: queryKeys.admin.locations(), queryFn: adminApi.locations });

  const departmentColumns: Column<AdminDepartment>[] = [
    {
      id: 'name',
      header: 'Department',
      cell: (row) => (
        <span>
          <span className="block text-small font-medium text-text-primary">{row.name}</span>
          <span className="font-mono text-caption text-text-muted">{row.code}</span>
        </span>
      ),
    },
    { id: 'status', header: 'Status', cell: (row) => <ActiveBadge active={row.active} /> },
  ];

  const locationColumns: Column<AdminLocation>[] = [
    {
      id: 'name',
      header: 'Location',
      cell: (row) => (
        <span>
          <span className="block text-small font-medium text-text-primary">{row.name}</span>
          <span className="font-mono text-caption text-text-muted">{row.code}</span>
        </span>
      ),
    },
    {
      id: 'place',
      header: 'City',
      cell: (row) =>
        [row.city, row.state, row.countryCode].filter(Boolean).join(', ') || (
          <span className="text-text-muted">—</span>
        ),
    },
    { id: 'status', header: 'Status', cell: (row) => <ActiveBadge active={row.active} /> },
  ];

  return (
    <div className="space-y-8">
      <SettingsCard
        title="Departments"
        description="Used on employee records and in reports."
        actions={
          canWrite && (
            <Button
              variant="primary"
              size="sm"
              leadingIcon={<Plus className="size-4" />}
              onClick={() => setDepartment('new')}
            >
              Add department
            </Button>
          )
        }
      >
        <DataTable
          caption="Departments"
          exportable={false}
          columns={departmentColumns}
          page={asPage(departments.data)}
          isLoading={departments.isLoading}
          error={departments.error}
          onRetry={() => departments.refetch()}
          getRowId={(row) => row.id}
          onRowClick={canWrite ? (row) => setDepartment(row) : undefined}
          empty={{ icon: <Building2 className="size-5" />, title: 'No departments yet' }}
        />
      </SettingsCard>

      <SettingsCard
        title="Locations"
        description="Where assets are stored, prepared and deployed."
        actions={
          canWrite && (
            <Button
              variant="primary"
              size="sm"
              leadingIcon={<Plus className="size-4" />}
              onClick={() => setLocation('new')}
            >
              Add location
            </Button>
          )
        }
      >
        <DataTable
          caption="Locations"
          exportable={false}
          columns={locationColumns}
          page={asPage(locations.data)}
          isLoading={locations.isLoading}
          error={locations.error}
          onRetry={() => locations.refetch()}
          getRowId={(row) => row.id}
          onRowClick={canWrite ? (row) => setLocation(row) : undefined}
          empty={{ icon: <MapPin className="size-5" />, title: 'No locations yet' }}
        />
      </SettingsCard>

      {!canWrite && (
        <p className="text-caption text-text-muted">
          You can view these. Changing them needs the organisation write permission.
        </p>
      )}

      <DepartmentDialog value={department} onClose={() => setDepartment(null)} />
      <LocationDialog value={location} onClose={() => setLocation(null)} />
    </div>
  );
}

interface DepartmentForm {
  code: string;
  name: string;
  active: boolean;
}

function DepartmentDialog({
  value,
  onClose,
}: {
  value: AdminDepartment | 'new' | null;
  onClose: () => void;
}) {
  const isNew = value === 'new';
  const existing = value && value !== 'new' ? value : null;
  const [otherError, setOtherError] = useState<unknown>(null);
  const { register, handleSubmit, reset, setError, formState } = useForm<DepartmentForm>();

  useEffect(() => {
    setOtherError(null);
    reset({ code: existing?.code ?? '', name: existing?.name ?? '', active: existing?.active ?? true });
  }, [value, existing, reset]);

  const save = useSettingsSave<DepartmentForm, AdminDepartment>({
    save: (form) =>
      existing
        ? adminApi.updateDepartment(existing.id, { name: form.name, active: form.active })
        : adminApi.createDepartment({ code: form.code, name: form.name }),
    invalidate: [queryKeys.admin.departments(), queryKeys.organization.departments()],
    successMessage: isNew ? 'Department added' : 'Department saved',
    setError,
    onDone: onClose,
    onOtherError: setOtherError,
  });

  return (
    <Dialog
      open={value !== null}
      onClose={onClose}
      title={isNew ? 'Add department' : `Edit ${existing?.name ?? 'department'}`}
      busy={save.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={handleSubmit((form) => save.mutate(form))}
          >
            {isNew ? 'Add department' : 'Save'}
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(event) => event.preventDefault()} noValidate>
        {otherError ? <InlineError error={otherError} /> : null}
        <TextInput
          label="Code"
          required={isNew}
          disabled={!isNew}
          placeholder="FINANCE"
          description={isNew ? 'Used by imports and HR feeds. It cannot be changed later.' : 'Codes cannot be changed.'}
          error={formState.errors.code?.message}
          {...register('code', {
            required: isNew ? 'A code is required' : false,
            pattern: { value: /^[A-Za-z0-9_-]+$/, message: 'Letters, digits, _ and - only' },
          })}
        />
        <TextInput
          label="Name"
          required
          error={formState.errors.name?.message}
          {...register('name', { required: 'A name is required' })}
        />
        {!isNew && (
          <Checkbox
            label="Active"
            description="Inactive departments are hidden from pickers. Existing records keep them."
            {...register('active')}
          />
        )}
      </form>
    </Dialog>
  );
}

interface LocationForm {
  code: string;
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  countryCode: string;
  postalCode: string;
  active: boolean;
}

export function LocationDialog({
  value,
  onClose,
}: {
  value: AdminLocation | 'new' | null;
  onClose: () => void;
}) {
  const isNew = value === 'new';
  const existing = value && value !== 'new' ? value : null;
  const [otherError, setOtherError] = useState<unknown>(null);
  const { register, handleSubmit, reset, setError, formState, watch, setValue } =
    useForm<LocationForm>();
  const countryCode = watch('countryCode');
  const stateName = watch('state');
  const countries = useMemo(() => countryOptions(), []);
  const inIndia = countryCode === 'IN';
  // A stored value that is not in the list (typed before the pickers existed) stays selectable.
  const stateOptions = Object.keys(INDIA_STATES);
  if (inIndia && stateName && !stateOptions.includes(stateName)) stateOptions.unshift(stateName);

  useEffect(() => {
    setOtherError(null);
    reset({
      code: existing?.code ?? '',
      name: existing?.name ?? '',
      addressLine1: existing?.addressLine1 ?? '',
      addressLine2: existing?.addressLine2 ?? '',
      city: existing?.city ?? '',
      state: existing?.state ?? '',
      countryCode: existing?.countryCode ?? (isNew ? 'IN' : ''),
      postalCode: existing?.postalCode ?? '',
      active: existing?.active ?? true,
    });
  }, [value, existing, reset]);

  const save = useSettingsSave<LocationForm, AdminLocation>({
    save: (form) => {
      const address = {
        addressLine1: form.addressLine1,
        addressLine2: form.addressLine2,
        city: form.city,
        state: form.state,
        countryCode: form.countryCode,
        postalCode: form.postalCode,
      };
      return existing
        ? adminApi.updateLocation(existing.id, { name: form.name, address, active: form.active })
        : adminApi.createLocation({ code: form.code, name: form.name, address });
    },
    invalidate: [queryKeys.admin.locations(), queryKeys.organization.locations()],
    successMessage: isNew ? 'Location added' : 'Location saved',
    setError,
    onDone: onClose,
    onOtherError: setOtherError,
  });

  return (
    <Dialog
      open={value !== null}
      onClose={onClose}
      title={isNew ? 'Add location' : `Edit ${existing?.name ?? 'location'}`}
      size="lg"
      busy={save.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={handleSubmit((form) => save.mutate(form))}
          >
            {isNew ? 'Add location' : 'Save'}
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(event) => event.preventDefault()} noValidate>
        {otherError ? <InlineError error={otherError} /> : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <TextInput
            label="Code"
            required={isNew}
            disabled={!isNew}
            placeholder="BLR-HQ"
            error={formState.errors.code?.message}
            {...register('code', {
              required: isNew ? 'A code is required' : false,
              pattern: { value: /^[A-Za-z0-9_-]+$/, message: 'Letters, digits, _ and - only' },
            })}
          />
          <TextInput
            label="Name"
            required
            error={formState.errors.name?.message}
            {...register('name', { required: 'A name is required' })}
          />
        </div>
        <TextInput label="Address line 1" {...register('addressLine1')} />
        <TextInput label="Address line 2" {...register('addressLine2')} />
        <div className="grid gap-4 sm:grid-cols-3">
          <Select
            label="Country"
            options={[{ value: '', label: 'Not set' }, ...countries]}
            {...register('countryCode', {
              onChange: () => {
                setValue('state', '');
                setValue('city', '');
              },
            })}
          />
          {inIndia ? (
            <Select
              label="State / union territory"
              options={[
                { value: '', label: 'Choose a state' },
                ...stateOptions.map((name) => ({ value: name, label: name })),
              ]}
              {...register('state', { onChange: () => setValue('city', '') })}
            />
          ) : (
            <TextInput label="State / region" {...register('state')} />
          )}
          {/* A suggestion list, not a closed choice: a town missing from the list can be typed. */}
          <TextInput
            label="City"
            list="location-city-options"
            autoComplete="off"
            placeholder={inIndia && !stateName ? 'Choose a state first' : undefined}
            {...register('city')}
          />
          <datalist id="location-city-options">
            {(inIndia ? (INDIA_STATES[stateName] ?? []) : []).map((city) => (
              <option key={city} value={city} />
            ))}
          </datalist>
        </div>
        <TextInput label="Postal code" className="sm:max-w-56" {...register('postalCode')} />
        {!isNew && (
          <Checkbox
            label="Active"
            description="Inactive locations are hidden from pickers. Assets already there keep it."
            {...register('active')}
          />
        )}
      </form>
    </Dialog>
  );
}
